import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { Download, Upload, FileSpreadsheet, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { AnalyticsService } from '../../../../../../core/services';
import { FormValidation } from '../../../../../../core/utility';
import { parseIgUsername } from '../../../../../../modules/brand/components/campaigns/postLinkRules';

const analyticsService = new AnalyticsService();
const formValidation = new FormValidation();

// Brand Pulse :: bulk add posts through an Excel sheet, so posts don't have to be added one by one.
// Flow: download template -> fill it -> upload -> preview valid / invalid rows -> insert in one call.
// Platform is detected from the link (same validator as the single Add Post form).

const MAX_ROWS = 500;
const PAGE_SIZE = 10;
const LINK_HEADER = 'Post Link';
const COST_HEADER = 'Cost (Optional)';
const USERNAME_HEADER = 'Username (Optional)';

type ParsedRow = { row: number; post_url: string; cost: number; platform: string; username?: string; error?: string };

const normalize = (k: string) => (k || '').toString().toLowerCase().replace(/[^a-z]/g, '');
const LINK_KEYS = ['postlink', 'posturl', 'link', 'url'];
const COST_KEYS = ['costoptional', 'cost'];
const USERNAME_KEYS = ['usernameoptional', 'username'];

// withUsername (admins): adds the optional Username column
function downloadTemplate(withUsername: boolean) {
  const header = withUsername ? [LINK_HEADER, COST_HEADER, USERNAME_HEADER] : [LINK_HEADER, COST_HEADER];
  const posts = XLSX.utils.aoa_to_sheet([header]);
  posts['!cols'] = withUsername ? [{ wch: 60 }, { wch: 16 }, { wch: 28 }] : [{ wch: 60 }, { wch: 16 }];
  const help = XLSX.utils.aoa_to_sheet([
    ['How to fill this sheet'],
    [`1. Add one post per row in the "Posts" sheet. Keep the header row as it is.`],
    [`2. "${LINK_HEADER}" is required - an Instagram, Facebook or YouTube post link.`],
    [`3. "${COST_HEADER}" is the amount spent on that post in INR (numbers only). Leave blank for 0.`],
    ...(withUsername
      ? [[`4. "${USERNAME_HEADER}" is the Instagram username of the creator who posted it (Instagram links only), e.g. creator_name. If Instagram does not show us a post, we use it to find the post on their profile. Leave blank if you do not have it.`]]
      : []),
    [`${withUsername ? 5 : 4}. Up to ${MAX_ROWS} posts per upload. Links already in the campaign are skipped automatically.`],
    [],
    ['Example rows (do not paste these into the Posts sheet)'],
    header,
    withUsername ? ['https://www.instagram.com/p/XXXXXXXXXXX/', 1500, 'creator_name'] : ['https://www.instagram.com/p/XXXXXXXXXXX/', 1500],
    ['https://www.youtube.com/watch?v=XXXXXXXXXXX', 5000],
    ['https://www.facebook.com/reel/000000000000000', ''],
  ]);
  help['!cols'] = [{ wch: 90 }, { wch: 16 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, posts, 'Posts');
  XLSX.utils.book_append_sheet(wb, help, 'Instructions');
  XLSX.writeFile(wb, 'brand_pulse_posts_template.xlsx');
}

function parseSheet(rows: any[], withUsername: boolean): ParsedRow[] {
  const seen = new Set<string>();
  return rows.map((raw, i) => {
    const entries = Object.entries(raw || {});
    const linkEntry = entries.find(([k]) => LINK_KEYS.includes(normalize(k)));
    const costEntry = entries.find(([k]) => COST_KEYS.includes(normalize(k)));
    const usernameEntry = entries.find(([k]) => USERNAME_KEYS.includes(normalize(k)));
    const rawUsername = withUsername ? (usernameEntry?.[1] ?? '').toString() : '';
    const raw_url = (linkEntry?.[1] ?? '').toString().trim();
    const costRaw = (costEntry?.[1] ?? '').toString().replace(/[,₹\s]/g, '');
    const cost = costRaw === '' ? 0 : Number(costRaw);
    const row = i + 2; // +1 for header, +1 for 1-based rows as shown in Excel

    if (!raw_url) return { row, post_url: raw_url, cost: 0, platform: '', error: 'Post link is empty' };
    const v: any = formValidation.validatePlatformUrl('all', raw_url);
    if (!v?.status) return { row, post_url: raw_url, cost: 0, platform: '', error: v?.message || 'Not a valid link' };
    // Canonical link from the validator (e.g. instagram.com/<user>/reel/<code> -> instagram.com/reel/<code>/)
    const post_url: string = v.url || raw_url;
    if (isNaN(cost) || cost < 0) return { row, post_url, cost: 0, platform: v.platform, error: 'Cost must be a positive number' };
    if (seen.has(post_url.toLowerCase())) return { row, post_url, cost, platform: v.platform, error: 'Repeated in file' };
    // optional, Instagram only; must be a real handle (the API refuses anything else)
    const parsedUsername = v.platform === 'Instagram' ? parseIgUsername(rawUsername) : { username: '' };
    if (parsedUsername.error) return { row, post_url, cost, platform: v.platform, error: parsedUsername.error };
    seen.add(post_url.toLowerCase());
    return { row, post_url, cost, platform: v.platform, username: parsedUsername.username };
  });
}

export function BulkAddPosts({ campaignId, onDone, withUsername = false }: { campaignId?: number; onDone: () => void; /** admins: Username column */ withUsername?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [skipped, setSkipped] = useState<{ post_url: string; reason: string }[]>([]);

  // Preview is paginated so a 200-500 row sheet stays usable inside the dialog
  const [page, setPage] = useState(0);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);

  const valid = rows.filter((r) => !r.error);
  const invalid = rows.filter((r) => r.error);
  const listed = showErrorsOnly ? invalid : rows;
  const pageCount = Math.max(1, Math.ceil(listed.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = listed.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const removeRow = (rowNo: number) => setRows((prev) => prev.filter((r) => r.row !== rowNo));

  const reset = () => {
    setFileName('');
    setRows([]);
    setPage(0);
    setShowErrorsOnly(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSkipped([]);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target?.result, { type: 'array' });
        // Prefer the template's "Posts" sheet; fall back to the first sheet for hand-made files.
        const sheet = wb.Sheets['Posts'] || wb.Sheets[wb.SheetNames[0]];
        const data: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        const parsed = parseSheet(data, withUsername).filter((r) => r.post_url || r.error !== 'Post link is empty');
        if (parsed.length === 0) {
          toast.error(`No posts found. Fill the "${LINK_HEADER}" column of the template.`);
          reset();
          return;
        }
        if (parsed.length > MAX_ROWS) {
          // OLD: toast.error(`You can upload at most ${MAX_ROWS} posts at a time.`);
          toast.error(`This file has ${parsed.length} posts - the limit is ${MAX_ROWS} per file. Please split it into multiple files.`);
          reset();
          return;
        }
        setFileName(file.name);
        setRows(parsed);
        setPage(0);
        setShowErrorsOnly(false);
      } catch {
        toast.error('Could not read this file. Please use the downloaded template (.xlsx).');
        reset();
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUpload = async () => {
    if (!campaignId || campaignId <= 0) {
      toast.error('Campaign ID is missing.');
      return;
    }
    if (valid.length === 0) return;
    setUploading(true);
    try {
      const res: any = await analyticsService.analyticsAddPostsBulk({
        campaign_id: campaignId,
        posts: valid.map(({ post_url, platform, cost, username }) => ({ post_url, platform, cost, username })),
      });
      const body = res; // http.postRequest resolves with the API response model itself
      if (body?.status === 1) {
        const inserted = body?.data?.inserted ?? 0;
        const skippedRows = body?.data?.skipped ?? [];
        setSkipped(skippedRows);
        if (inserted > 0) toast.success(`${inserted} post${inserted > 1 ? 's' : ''} added`);
        if (skippedRows.length > 0) toast.warning(`${skippedRows.length} skipped - already added to this campaign`);
        reset();
        if (inserted > 0) onDone();
      } else {
        toast.error(body?.info?.startsWith('ERROR') ? 'Bulk upload failed. Please try again.' : body?.info || 'Bulk upload failed');
      }
    } catch {
      toast.error('Bulk upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid gap-3">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span className="h-px flex-1 bg-gray-200" />
        or add many posts with Excel
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => downloadTemplate(withUsername)} type="button">
          <Download className="h-4 w-4 mr-1" />
          Download Template
        </Button>
        <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} type="button" disabled={uploading}>
          <Upload className="h-4 w-4 mr-1" />
          Upload Excel
        </Button>
        <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
      </div>
      <p className="text-xs text-gray-500 -mt-1">
        Up to <span className="font-medium text-gray-700">{MAX_ROWS} posts per file</span>. Have more? Split them into multiple files.
      </p>

      {rows.length > 0 && (
        <div className="rounded-md border border-gray-200 p-3 text-sm grid gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FileSpreadsheet className="h-4 w-4 shrink-0 text-green-700" />
            <span className="truncate font-medium">{fileName}</span>
            <button type="button" onClick={reset} className="ml-auto text-gray-400 hover:text-gray-600" aria-label="Remove file">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-green-700 font-medium">{valid.length} ready to add</span>
            {invalid.length > 0 && <span className="text-red-600 font-medium">· {invalid.length} with errors (will be skipped)</span>}
            {invalid.length > 0 && (
              <label className="ml-auto flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showErrorsOnly}
                  onChange={(e) => {
                    setShowErrorsOnly(e.target.checked);
                    setPage(0);
                  }}
                />
                Errors only
              </label>
            )}
          </div>

          {/* OLD: only the error rows were listed (unpaginated); now every row, 10 per page */}
          <ul className="grid gap-1 text-xs">
            {pageRows.map((r) => (
              <li key={r.row} className="flex items-start gap-2 rounded border border-gray-100 px-2 py-1">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-gray-900" title={r.post_url}>{r.post_url || '(empty link)'}</div>
                  <div className="text-gray-500">
                    Row {r.row}
                    {r.platform ? ` · ${r.platform}` : ''}
                    {!r.error && r.cost ? ` · ₹${r.cost}` : ''}
                    {!r.error && r.username ? ` · @${r.username}` : ''}
                    {' · '}
                    {r.error ? <span className="text-red-600">{r.error}</span> : <span className="text-green-700">Ready</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(r.row)}
                  className="shrink-0 text-gray-400 hover:text-red-600"
                  aria-label={`Remove row ${r.row}`}
                  title="Remove this row"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>

          {listed.length > PAGE_SIZE && (
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>
                {safePage * PAGE_SIZE + 1}-{Math.min((safePage + 1) * PAGE_SIZE, listed.length)} of {listed.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={safePage === 0}
                  onClick={() => setPage(safePage - 1)}
                  className="rounded border border-gray-200 p-1 disabled:opacity-40"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-1">Page {safePage + 1} / {pageCount}</span>
                <button
                  type="button"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage(safePage + 1)}
                  className="rounded border border-gray-200 p-1 disabled:opacity-40"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading || valid.length === 0}
            className="btn btn-primary btn-sm flex align-items-center justify-center disabled:opacity-60"
          >
            {uploading ? 'Adding posts...' : `Add ${valid.length} Post${valid.length === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      {skipped.length > 0 && (
        <div className="rounded-md bg-amber-50 p-2 text-xs text-amber-800 grid gap-1">
          <span className="font-medium">Skipped (already in this campaign):</span>
          <ul className="max-h-24 overflow-y-auto grid gap-1">
            {skipped.map((s, i) => (
              <li key={i} className="break-all">{s.post_url}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
